'use client'

import { useRef } from 'react'
import { usePauseMediaOnHidden } from '~/lib/hooks/usePauseMediaOnHidden'

interface WatchVideoPlayerProps {
  contentPath: string
  posterPath: string
  width: number
  height: number
}

/**
 * The watch-page video player. It stays a `controls` `<video>` rendered in the
 * SSR HTML (no client JS gate — crawlable, first paint shows the poster frame);
 * this thin client wrapper only adds the Cache Components requirement that the
 * audio-capable player PAUSE when its route is hidden by `<Activity>`
 * (`display:none` does not stop playback, so without this the audio keeps
 * playing after the user navigates away). Playback position is preserved, so
 * the user can resume from where they left off on return.
 */
export function WatchVideoPlayer({
  contentPath,
  posterPath,
  width,
  height,
}: WatchVideoPlayerProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null)
  usePauseMediaOnHidden(videoRef)

  return (
    <video
      ref={videoRef}
      controls
      playsInline
      preload="metadata"
      poster={posterPath}
      width={width}
      height={height}
      className="h-auto w-full"
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      <source src={contentPath} type="video/mp4" />
      {/* Crawlable fallback link to the raw asset for clients that can't play
          inline (and an honest no-JS experience). */}
      <a href={contentPath}>Download the video (MP4)</a>
    </video>
  )
}
